import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { supabase } from '../../lib/supabase';

const inputClass = 'bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-darker text-sm focus:outline-none focus:border-yellow duration-300';

const categories = [
     'fuel', 'maintenance', 'equipment_purchase', 'insurance', 'marketing',
     'supplies', 'vehicle', 'rent', 'utilities', 'payroll', 'software', 'other',
];

const categoryLabels = {
     fuel: 'Fuel', maintenance: 'Maintenance', equipment_purchase: 'Equipment Purchase',
     insurance: 'Insurance', marketing: 'Marketing', supplies: 'Supplies',
     vehicle: 'Vehicle', rent: 'Rent', utilities: 'Utilities',
     payroll: 'Payroll', software: 'Software', other: 'Other',
};

const categoryColors = {
     fuel: 'bg-orange-500/20 text-orange-400',
     maintenance: 'bg-blue-500/20 text-blue-400',
     equipment_purchase: 'bg-purple-500/20 text-purple-400',
     insurance: 'bg-cyan-500/20 text-cyan-400',
     marketing: 'bg-pink-500/20 text-pink-400',
     supplies: 'bg-green-500/20 text-green-400',
     vehicle: 'bg-amber-500/20 text-amber-400',
     rent: 'bg-red-500/20 text-red-400',
     utilities: 'bg-teal-500/20 text-teal-400',
     payroll: 'bg-indigo-500/20 text-indigo-400',
     software: 'bg-violet-500/20 text-violet-400',
     other: 'bg-gray-500/20 text-gray-400',
};

const emptyForm = {
     date: format(new Date(), 'yyyy-MM-dd'),
     category: 'fuel',
     description: '',
     amount: '',
     vendor: '',
     receipt_note: '',
};

export default function Expenses() {
     const [expenses, setExpenses] = useState([]);
     const [loading, setLoading] = useState(true);
     const [showForm, setShowForm] = useState(false);
     const [formData, setFormData] = useState(emptyForm);
     const [editingId, setEditingId] = useState(null);
     const [filter, setFilter] = useState('all');
     const [saving, setSaving] = useState(false);

     useEffect(() => {
          fetchExpenses();
          // eslint-disable-next-line
     }, []);

     async function fetchExpenses() {
          setLoading(true);
          const { data } = await supabase
               .from('expenses')
               .select('*')
               .order('date', { ascending: false });
          setExpenses(data || []);
          setLoading(false);
     }

     async function handleSubmit(e) {
          e.preventDefault();
          setSaving(true);
          const payload = {
               date: formData.date,
               category: formData.category,
               description: formData.description,
               amount_cents: Math.round(parseFloat(formData.amount || 0) * 100),
               vendor: formData.vendor || null,
               receipt_note: formData.receipt_note || null,
          };

          if (editingId) {
               await supabase.from('expenses').update(payload).eq('id', editingId);
          } else {
               await supabase.from('expenses').insert(payload);
          }

          setSaving(false);
          setShowForm(false);
          setEditingId(null);
          setFormData(emptyForm);
          fetchExpenses();
     }

     function handleEdit(expense) {
          setFormData({
               date: expense.date,
               category: expense.category,
               description: expense.description || '',
               amount: (expense.amount_cents / 100).toFixed(2),
               vendor: expense.vendor || '',
               receipt_note: expense.receipt_note || '',
          });
          setEditingId(expense.id);
          setShowForm(true);
     }

     async function handleDelete(id) {
          if (!window.confirm('Delete this expense?')) return;
          await supabase.from('expenses').delete().eq('id', id);
          fetchExpenses();
     }

     function handleCancel() {
          setShowForm(false);
          setEditingId(null);
          setFormData(emptyForm);
     }

     const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
     const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');
     const monthlyTotal = expenses
          .filter(e => e.date >= monthStart && e.date <= monthEnd)
          .reduce((s, e) => s + (e.amount_cents || 0), 0);

     const filtered = filter === 'all' ? expenses : expenses.filter(e => e.category === filter);

     const fmt = (cents) => `$${(cents / 100).toLocaleString()}`;

     return (
          <div>
               <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
                    <h1 className='text-darker text-2xl font-bold'>Expenses</h1>
                    <button
                         onClick={() => { showForm ? handleCancel() : setShowForm(true); }}
                         className='bg-yellow text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow/90 duration-300'
                    >
                         {showForm ? 'Cancel' : '+ Add Expense'}
                    </button>
               </div>

               {/* Monthly Total */}
               <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                    <p className='text-darker/40 text-sm'>This Month&apos;s Expenses</p>
                    <p className='text-red-400 text-3xl font-bold mt-1'>{fmt(monthlyTotal)}</p>
               </div>

               {/* Add/Edit Form */}
               {showForm && (
                    <form onSubmit={handleSubmit} className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                         <h2 className='text-darker text-lg font-semibold mb-4'>
                              {editingId ? 'Edit Expense' : 'Add Expense'}
                         </h2>
                         <div className='grid grid-cols-1 tablet:grid-cols-3 gap-4'>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Date *</label>
                                   <input
                                        type='date'
                                        required
                                        value={formData.date}
                                        onChange={e => setFormData({ ...formData, date: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   />
                              </div>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Category *</label>
                                   <select
                                        required
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   >
                                        {categories.map(c => (
                                             <option key={c} value={c}>{categoryLabels[c]}</option>
                                        ))}
                                   </select>
                              </div>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Amount ($) *</label>
                                   <input
                                        type='number'
                                        step='0.01'
                                        min='0'
                                        required
                                        placeholder='0.00'
                                        value={formData.amount}
                                        onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   />
                              </div>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Description *</label>
                                   <input
                                        type='text'
                                        required
                                        placeholder='What was this expense for?'
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   />
                              </div>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Vendor</label>
                                   <input
                                        type='text'
                                        placeholder='Optional'
                                        value={formData.vendor}
                                        onChange={e => setFormData({ ...formData, vendor: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   />
                              </div>
                              <div>
                                   <label className='block text-darker/40 text-sm mb-1'>Receipt Note</label>
                                   <input
                                        type='text'
                                        placeholder='Optional'
                                        value={formData.receipt_note}
                                        onChange={e => setFormData({ ...formData, receipt_note: e.target.value })}
                                        className={`${inputClass} w-full`}
                                   />
                              </div>
                         </div>
                         <div className='mt-4'>
                              <button
                                   type='submit'
                                   disabled={saving}
                                   className='bg-yellow text-black px-5 py-2 rounded-lg text-sm font-semibold hover:bg-yellow/90 duration-300 disabled:opacity-50'
                              >
                                   {saving ? 'Saving...' : editingId ? 'Update Expense' : 'Add Expense'}
                              </button>
                         </div>
                    </form>
               )}

               {/* Category Filter Tabs */}
               <div className='flex flex-wrap gap-2 mb-6'>
                    <button
                         onClick={() => setFilter('all')}
                         className={`px-3 py-1.5 rounded-lg text-sm font-medium duration-300 ${
                              filter === 'all' ? 'bg-yellow text-black' : 'bg-gray-50 text-darker/60 hover:text-darker'
                         }`}
                    >
                         All
                    </button>
                    {categories.map(c => (
                         <button
                              key={c}
                              onClick={() => setFilter(c)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium duration-300 ${
                                   filter === c ? 'bg-yellow text-black' : 'bg-gray-50 text-darker/60 hover:text-darker'
                              }`}
                         >
                              {categoryLabels[c]}
                         </button>
                    ))}
               </div>

               {/* Expenses List */}
               {loading ? (
                    <p className='text-darker/60'>Loading expenses...</p>
               ) : filtered.length === 0 ? (
                    <p className='text-darker/40 text-sm'>No expenses found.</p>
               ) : (
                    <div className='space-y-3'>
                         {filtered.map(expense => (
                              <div key={expense.id} className='bg-white rounded-xl p-4 border border-gray-200 flex flex-wrap items-center gap-4'>
                                   <div className='text-darker/60 text-sm w-24 shrink-0'>
                                        {format(new Date(expense.date + 'T00:00:00'), 'MMM d, yyyy')}
                                   </div>
                                   <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[expense.category] || categoryColors.other}`}>
                                        {categoryLabels[expense.category] || expense.category}
                                   </span>
                                   <div className='flex-1 min-w-0'>
                                        <p className='text-darker text-sm truncate'>{expense.description}</p>
                                        {expense.vendor && (
                                             <p className='text-darker/40 text-xs'>{expense.vendor}</p>
                                        )}
                                   </div>
                                   <p className='text-red-400 text-sm font-semibold whitespace-nowrap'>
                                        {fmt(expense.amount_cents)}
                                   </p>
                                   <div className='flex gap-2'>
                                        <button
                                             onClick={() => handleEdit(expense)}
                                             className='text-darker/40 hover:text-yellow text-sm duration-300'
                                        >
                                             Edit
                                        </button>
                                        <button
                                             onClick={() => handleDelete(expense.id)}
                                             className='text-darker/40 hover:text-red-400 text-sm duration-300'
                                        >
                                             Delete
                                        </button>
                                   </div>
                              </div>
                         ))}
                    </div>
               )}
          </div>
     );
}
